/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Modal } from '../modals/Modal';
import { Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, ticketColors } from '../theme/public-api';
import { ticketChrome as ticketUi, TicketModalHeader } from '../ticket/public-api';

type FilterSheetModalProps = {
  visible: boolean;
  onClose: () => void;
  onApply: () => void;
  onClear: () => void;
  hasActiveDraft: boolean;
  title?: string;
  children: React.ReactNode;
  applyLabel?: string;
  /** Ticket chrome (POS filters clean). */
  appearance?: 'default' | 'ticket';
  /** Kicker + subtítulo del header nuevo (con appearance="ticket"). */
  kicker?: string;
  subtitle?: string;
};

export function FilterSheetModal({
  visible,
  onClose,
  onApply,
  onClear,
  hasActiveDraft,
  title = 'Filtros',
  children,
  applyLabel = 'Aplicar',
  appearance = 'default',
  kicker,
  subtitle,
}: FilterSheetModalProps) {
  const insets = useSafeAreaInsets();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const ticket = appearance === 'ticket';
  const wide = screenWidth >= 768;
  /** PC: centered paper dialog; mobile: bottom sheet. */
  const asDialog = ticket && wide;
  const sheetMaxHeight = screenHeight * (ticket ? 0.92 : 0.86);

  return (
    <Modal
      visible={visible}
      transparent
      animationType={asDialog ? 'fade' : 'slide'}
      onRequestClose={onClose}
    >
      <View
        style={[
          ticket ? ticketUi.sheetBackdrop : styles.backdrop,
          asDialog && styles.backdropDialog,
        ]}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View
          style={[
            asDialog ? ticketUi.dialogCard : styles.sheet,
            ticket && !asDialog && styles.sheetTicket,
            asDialog && styles.sheetTicketDialog,
            {
              paddingBottom: asDialog ? 0 : Math.max(insets.bottom, spacing.sm),
              maxHeight: sheetMaxHeight,
            },
          ]}
        >
          {!asDialog ? <View style={[styles.grabber, ticket && styles.grabberTicket]} /> : null}

          {ticket && kicker ? (
            <TicketModalHeader
              kicker={kicker}
              title={title}
              subtitle={subtitle}
              onClose={onClose}
              compact={!asDialog}
            />
          ) : (
            <View style={ticket ? [ticketUi.sheetHeader, asDialog && styles.headerDialog] : styles.header}>
              <Text style={ticket ? ticketUi.sheetTitle : styles.headerTitle}>{title}</Text>
              <TouchableOpacity
                onPress={onClose}
                style={ticket ? ticketUi.sheetCloseBtn : styles.closeBtn}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                {ticket ? (
                  <Text style={ticketUi.sheetCloseGlyph}>×</Text>
                ) : (
                  <Ionicons name="close" size={16} color={colors.textMuted} />
                )}
              </TouchableOpacity>
            </View>
          )}

          <ScrollView
            style={[styles.scroll, { maxHeight: screenHeight * (asDialog ? 0.62 : 0.58) }]}
            contentContainerStyle={[styles.scrollContent, ticket && styles.scrollContentTicket]}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
          >
            {children}
          </ScrollView>

          <View style={ticket ? ticketUi.foot : styles.footer}>
            <TouchableOpacity
              style={[
                ticket ? [ticketUi.btnGhost, styles.footerBtnFlex] : styles.clearBtn,
                !hasActiveDraft && styles.clearBtnQuiet,
              ]}
              onPress={onClear}
              activeOpacity={0.55}
              accessibilityRole="button"
              accessibilityLabel="Limpiar todos los filtros"
              disabled={!hasActiveDraft}
            >
              <Text
                style={[
                  ticket ? ticketUi.btnGhostText : styles.clearBtnText,
                  !hasActiveDraft && styles.clearBtnTextQuiet,
                ]}
              >
                Limpiar
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={ticket ? [ticketUi.btnGold, styles.footerBtnFlex] : styles.applyBtn}
              onPress={onApply}
              activeOpacity={0.55}
              accessibilityRole="button"
              accessibilityLabel={applyLabel}
            >
              <Text style={ticket ? ticketUi.btnGoldText : styles.applyBtnText}>{applyLabel}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'flex-end',
  },
  backdropDialog: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  sheet: {
    backgroundColor: colors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 8,
    borderTopWidth: 1,
    borderColor: colors.borderSoft,
    overflow: 'hidden',
    zIndex: 1,
  },
  sheetTicket: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderColor: ticketColors.ruleSoft,
  },
  sheetTicketDialog: {
    width: '100%',
    maxWidth: 420,
    zIndex: 1,
  },
  grabber: {
    width: 28,
    height: 3,
    borderRadius: 2,
    backgroundColor: colors.controlBorder,
    alignSelf: 'center',
    marginTop: 2,
    marginBottom: 12,
  },
  grabberTicket: {
    width: 28,
    height: 3,
    backgroundColor: ticketColors.ruleSoft,
    marginTop: 2,
    marginBottom: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    marginBottom: 8,
  },
  headerDialog: {
    paddingTop: 16,
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: colors.inputBackground,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  scroll: {
    flexGrow: 0,
    flexShrink: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.md,
    gap: 18,
  },
  scrollContentTicket: {
    paddingHorizontal: 16,
    paddingBottom: 8,
    gap: 16,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 4,
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.borderSoft,
  },
  footerBtnFlex: {
    flex: 1,
  },
  clearBtn: {
    height: 48,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.borderSoft,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    flex: 1,
  },
  clearBtnQuiet: {
    opacity: 0.45,
  },
  clearBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.textMuted,
  },
  clearBtnTextQuiet: {
    color: colors.textMuted,
  },
  applyBtn: {
    flex: 1,
    height: 48,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  applyBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.goldInk,
  },
});
